import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Slider, Switch, Tooltip } from "antd";
import { AlertTriangle, EyeOff, Maximize2, Minimize2, Pause, Play, RotateCcw, X } from "lucide-react";
import { Terminal } from "xterm";
import "xterm/css/xterm.css";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import {
  castCols,
  castDuration,
  castRows,
  formatClock,
  isRedactionMarker,
  parseAsciicast,
  selectMarkers,
  selectPlayableEvents,
} from "./asciicast";

// Session player for asciicast v2 recordings produced by the SSH proxy.
//
// xterm is already a dependency; no player library is added. The terminal is
// sized from the recording's own header, and `o` events are written on a timer
// driven by their timestamps.
//
// Seeking backwards replays from the start into a cleared terminal — a terminal
// stream has no keyframe to jump to, and these files are kilobytes, so the
// replay is instant.
//
// Fitting: the recording's cols × rows never change (reflowing a recorded
// stream to a different width garbles it). The terminal renders once at a
// fixed font, and the whole rendered screen is SCALED (CSS transform) to fit
// the space left on screen, so the full console is always visible with no
// scrolling. The dark frame hugs the scaled console rather than stretching to
// the page width. Scaling is computed from layout sizes, which transforms don't
// affect, so it is exact in one step — no re-measure loop.

const SPEEDS = [1, 2, 4];
// Never crop a recording below this many rows, so a one-line session still
// reads as a terminal.
const MIN_SHOWN_ROWS = 6;
// Padding inside the full-screen container (top and bottom).
const FULLSCREEN_PAD = 16;

// The lowest screen row (0-based) the session ever reached — by the cursor or
// by any text — found by replaying the stream once into a terminal that is
// never displayed. Rows below it were never touched, so the player can hide
// them. Empty rows above it are kept: only the maximum is tracked. A session
// that reaches the bottom (it scrolled, or ran vim/top/less) keeps its full
// height. A few kilobytes of events replay in milliseconds.
function lowestRowUsed(events, cols, rows) {
  return new Promise((resolve) => {
    if (!events.length) return resolve(0);
    const t = new Terminal({ cols, rows, scrollback: 0 });
    let lowest = 0;
    const check = () => {
      if (lowest >= rows - 1) return;
      const b = t.buffer.active;
      let low = b.cursorY;
      for (let y = rows - 1; y > low; y -= 1) {
        const line = b.getLine(b.baseY + y);
        if (line && line.translateToString(true).trim()) {
          low = y;
          break;
        }
      }
      if (low > lowest) lowest = low;
    };
    events.forEach((e) => t.write(e.data, check));
    t.write("", () => {
      t.dispose();
      resolve(lowest);
    });
  });
}
const BASE_FONT = 14;
const MAX_SCALE = 1.5;
const MAX_SCALE_FULLSCREEN = 2.5;
const BOX_PAD = 10;
const BOX_BORDER = 1;
// Frame chrome around the scaled console, per axis.
const FRAME = BOX_PAD * 2 + BOX_BORDER * 2;
// Space under the terminal besides the transport row itself (the grid gap
// and a little breathing room). The row's own height is measured, because it
// wraps onto two lines on narrow screens.
const TRANSPORT_EXTRA = 12 + 16;
// The app layout's sticky footer sits over the bottom of the page.
const footerHeight = () => document.querySelector(".ant-layout-footer")?.getBoundingClientRect().height || 0;
const fullscreenElement = () => document.fullscreenElement || document.webkitFullscreenElement || null;
const MIN_BOX_HEIGHT = 260;

const TERM_THEME = {
  background: "#0B0D12",
  foreground: "#E6E8EC",
  cursor: "#E6E8EC",
  selectionBackground: "#2B3FE8",
};

function HeaderField({ label, children, mono = true, wide = false }) {
  return (
    <div style={{ minWidth: 0, gridColumn: wide ? "span 2" : undefined }}>
      <div
        style={{
          fontFamily: AN_MONO,
          fontSize: 9.5,
          letterSpacing: 0.9,
          textTransform: "uppercase",
          color: AN_COLORS.muted,
          marginBottom: 3,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: mono ? AN_MONO : AN_FONT,
          fontSize: 12,
          color: AN_COLORS.ink,
          wordBreak: "break-all",
        }}
      >
        {children || "—"}
      </div>
    </div>
  );
}

export default function CastPlayer({ text }) {
  const hostRef = useRef(null);
  const areaRef = useRef(null); // the space the console may use
  const playerRef = useRef(null); // terminal + transport; what goes full screen
  const transportRef = useRef(null);
  const [boxHeight, setBoxHeight] = useState(420);
  // Read by fit(): the height the console MAY use. The area itself shrinks to
  // the frame, so its own height can't be the limit or it could never grow.
  const boxHeightRef = useRef(420);
  boxHeightRef.current = boxHeight;
  const [natural, setNatural] = useState({ w: 0, h: 0 }); // unscaled screen size (after cropping)
  // Rows actually shown: the recording's rows, less the untouched ones at the
  // bottom. The terminal itself keeps the full height so playback is exact;
  // only the frame hides the rest.
  const [shownRows, setShownRows] = useState(null);
  const shownRowsRef = useRef(null);
  shownRowsRef.current = shownRows;
  const rowsRef = useRef(0);
  const [scale, setScale] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Fallback when the browser refuses native full screen (embedded or locked-
  // down browsers): the player covers the whole window instead.
  const [windowFill, setWindowFill] = useState(false);
  const windowFillRef = useRef(false);
  windowFillRef.current = windowFill;
  const measureRef = useRef(null);
  // Scroll positions of the player's ancestors, saved on entering full screen
  // and restored on leaving it, so the page comes back where it was.
  const savedScrollRef = useRef([]);
  // Height of the player when it left the page, held by a placeholder while it
  // fills the window so the page underneath doesn't reflow.
  const [placeholderH, setPlaceholderH] = useState(0);
  const termRef = useRef(null);
  const rafRef = useRef(null);
  const cursorRef = useRef(0);
  const positionRef = useRef(0);
  const lastFrameRef = useRef(0);

  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [position, setPosition] = useState(0);
  const [showKeystrokes, setShowKeystrokes] = useState(false);

  const { header, events, errors } = useMemo(() => parseAsciicast(text), [text]);
  const duration = useMemo(() => castDuration(events), [events]);
  const markers = useMemo(() => selectMarkers(events), [events]);
  const playable = useMemo(
    () => selectPlayableEvents(events, { showKeystrokes }),
    [events, showKeystrokes],
  );

  const cols = castCols(header);
  const rows = castRows(header);
  rowsRef.current = rows;

  // Work out how many rows the session actually used (see lowestRowUsed).
  // Measured over the stream WITH keystrokes, a superset of what plays either
  // way, so toggling "Show keystrokes" can never push text below the crop.
  const allPlayable = useMemo(() => selectPlayableEvents(events, { showKeystrokes: true }), [events]);
  useEffect(() => {
    let live = true;
    setShownRows(null);
    lowestRowUsed(allPlayable, cols, rows).then((low) => {
      // One spare row under the lowest used, so the cursor isn't on the edge.
      if (live) setShownRows(Math.min(rows, Math.max(MIN_SHOWN_ROWS, low + 2)));
    });
    return () => {
      live = false;
    };
  }, [allPlayable, cols, rows]);

  // ── terminal lifecycle ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!hostRef.current) return undefined;
    const term = new Terminal({
      cols,
      rows,
      convertEol: false,
      disableStdin: true,
      cursorBlink: false,
      fontFamily: AN_MONO,
      fontSize: BASE_FONT,
      theme: TERM_THEME,
      // The recording is replayed at its own rows; nothing scrolls inside the
      // frame, so no scrollback (and no scrollbar changing the width).
      scrollback: 0,
    });
    term.open(hostRef.current);
    termRef.current = term;
    return () => {
      term.dispose();
      termRef.current = null;
    };
  }, [cols, rows]);

  // ── fit to the screen ───────────────────────────────────────────────────────
  // The box gets whatever viewport height is left below its top edge — less
  // the controls and, outside full screen, the layout's sticky footer.
  useLayoutEffect(() => {
    const measure = () => {
      const el = areaRef.current;
      if (!el) return;
      const fs = (!!playerRef.current && fullscreenElement() === playerRef.current) || windowFillRef.current;
      setIsFullscreen(fs);
      // Measured as if the page were scrolled to the top, so the console's
      // size doesn't depend on where the page happens to be scrolled.
      // In full screen the player is centred, so the area's own position moves
      // with its size; measure from the container's fixed padding instead, or
      // the two would chase each other.
      let top = fs ? FULLSCREEN_PAD : el.getBoundingClientRect().top;
      if (!fs) for (let n = el.parentElement; n; n = n.parentElement) top += n.scrollTop;
      const transportH = transportRef.current?.getBoundingClientRect().height || 44;
      const reserve = transportH + TRANSPORT_EXTRA + (fs ? FULLSCREEN_PAD : footerHeight());
      setBoxHeight(Math.max(MIN_BOX_HEIGHT, Math.floor(window.innerHeight - top - reserve)));
    };
    // Synchronous, like the fit: a background tab gets no animation frames.
    const later = () => measure();
    measureRef.current = measure;
    measure();
    // The transport row changes height when it wraps.
    const ro = new ResizeObserver(later);
    if (transportRef.current) ro.observe(transportRef.current);
    window.addEventListener("resize", later);
    document.addEventListener("fullscreenchange", later);
    document.addEventListener("webkitfullscreenchange", later);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", later);
      document.removeEventListener("fullscreenchange", later);
      document.removeEventListener("webkitfullscreenchange", later);
    };
  }, []);

  // Full screen, like a video player: the terminal and every control, with a
  // close button in the corner. Esc leaves it natively.
  const toggleFullscreen = useCallback(() => {
    const el = playerRef.current;
    if (!el) return;
    if (windowFillRef.current) {
      setWindowFill(false);
      return;
    }
    if (fullscreenElement()) {
      (document.exitFullscreen || document.webkitExitFullscreen)?.call(document);
      return;
    }
    savedScrollRef.current = [];
    for (let n = el.parentElement; n; n = n.parentElement) {
      if (n.scrollTop || n.scrollLeft) savedScrollRef.current.push([n, n.scrollTop, n.scrollLeft]);
    }
    el.focus({ preventScroll: true });
    const req = el.requestFullscreen || el.webkitRequestFullscreen;
    const fallback = () => {
      if (fullscreenElement() || windowFillRef.current) return;
      setPlaceholderH(el.getBoundingClientRect().height);
      setWindowFill(true);
    };
    if (!req) return fallback();
    try {
      const p = req.call(el);
      if (p && typeof p.catch === "function") p.catch(fallback);
    } catch {
      fallback();
    }
    // Some embedded browsers neither enter full screen nor reject the request.
    setTimeout(fallback, 300);
  }, []);

  const restoreScroll = useCallback(() => {
    const saved = savedScrollRef.current;
    savedScrollRef.current = [];
    // After layout settles back (two frames), or the position is clamped.
    const apply = () => saved.forEach(([n, top, left]) => n.scrollTo(left, top));
    apply();
    requestAnimationFrame(() => requestAnimationFrame(apply));
  }, []);

  // Native full screen: restore the page's scroll on leaving it.
  useEffect(() => {
    const onChange = () => !fullscreenElement() && savedScrollRef.current.length && restoreScroll();
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, [restoreScroll]);

  // Window-fill mode: re-measure when it toggles, and let Esc leave it (native
  // full screen handles Esc itself).
  useLayoutEffect(() => {
    measureRef.current?.();
    if (!windowFill) return undefined;
    const restoreOnLeave = () => restoreScroll();
    const onEsc = (e) => e.key === "Escape" && setWindowFill(false);
    window.addEventListener("keydown", onEsc);
    return () => {
      window.removeEventListener("keydown", onEsc);
      restoreOnLeave();
    };
  }, [windowFill, restoreScroll]);

  // Scale = the largest factor at which the unscaled screen fits the area.
  // Runs synchronously from a ResizeObserver (no animation frames), whenever
  // the area or the terminal's own screen changes size.
  const fit = useCallback(() => {
    const area = areaRef.current;
    const screen = hostRef.current?.querySelector(".xterm-screen");
    if (!area || !screen) return;
    const w = screen.offsetWidth;
    const shown = shownRowsRef.current;
    const total = rowsRef.current;
    const h = shown && total ? (screen.offsetHeight * shown) / total : screen.offsetHeight;
    const availW = area.clientWidth - FRAME;
    const availH = boxHeightRef.current - FRAME;
    if (!w || !h || availW <= 0 || availH <= 0) return;
    const max = fullscreenElement() === playerRef.current || windowFillRef.current ? MAX_SCALE_FULLSCREEN : MAX_SCALE;
    const next = Math.min(max, availW / w, availH / h);
    setNatural((n) => (n.w === w && n.h === h ? n : { w, h }));
    setScale((s0) => (Math.abs(s0 - next) < 0.001 ? s0 : next));
  }, []);

  useLayoutEffect(() => {
    const area = areaRef.current;
    const screen = hostRef.current?.querySelector(".xterm-screen");
    if (!area) return undefined;
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(area);
    if (screen) ro.observe(screen);
    return () => ro.disconnect();
  }, [fit, cols, rows, boxHeight, shownRows]);

  // Writes every event up to `t` without delay. Used both by the play loop
  // (which advances `t` smoothly) and by a forward seek.
  const flushTo = useCallback(
    (t) => {
      const term = termRef.current;
      if (!term) return;
      let chunk = "";
      while (cursorRef.current < playable.length && playable[cursorRef.current].time <= t) {
        chunk += playable[cursorRef.current].data;
        cursorRef.current += 1;
      }
      if (chunk) term.write(chunk);
    },
    [playable],
  );

  const seek = useCallback(
    (t) => {
      const target = Math.max(0, Math.min(t, duration));
      const term = termRef.current;
      if (term && target < positionRef.current) {
        term.reset();
        cursorRef.current = 0;
      }
      positionRef.current = target;
      setPosition(target);
      flushTo(target);
    },
    [duration, flushTo],
  );

  // Changing the keystroke toggle changes which events exist, so the stream has
  // to be rebuilt from the start rather than patched.
  useEffect(() => {
    const term = termRef.current;
    if (!term) return;
    term.reset();
    cursorRef.current = 0;
    const at = positionRef.current;
    let chunk = "";
    while (cursorRef.current < playable.length && playable[cursorRef.current].time <= at) {
      chunk += playable[cursorRef.current].data;
      cursorRef.current += 1;
    }
    if (chunk) term.write(chunk);
  }, [playable]);

  // ── play loop ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!playing) return undefined;
    lastFrameRef.current = performance.now();
    const frame = (now) => {
      const dt = ((now - lastFrameRef.current) / 1000) * speed;
      lastFrameRef.current = now;
      const next = positionRef.current + dt;
      positionRef.current = next;
      flushTo(next);
      setPosition(Math.min(next, duration));
      if (next >= duration) {
        setPlaying(false);
        positionRef.current = duration;
        return;
      }
      rafRef.current = requestAnimationFrame(frame);
    };
    rafRef.current = requestAnimationFrame(frame);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [playing, speed, duration, flushTo]);

  const restart = () => {
    setPlaying(false);
    const term = termRef.current;
    if (term) term.reset();
    cursorRef.current = 0;
    positionRef.current = 0;
    setPosition(0);
  };

  const ended = duration > 0 && position >= duration;

  const togglePlay = () => {
    if (!playing && positionRef.current >= duration) restart();
    setPlaying((p) => !p);
  };

  // Space plays/pauses and F toggles full screen while the player has focus.
  const onKeyDown = (e) => {
    if (e.target !== e.currentTarget && ["BUTTON", "INPUT"].includes(e.target.tagName)) return;
    if (e.key === " ") {
      e.preventDefault();
      togglePlay();
    } else if (e.key === "f" || e.key === "F") {
      e.preventDefault();
      toggleFullscreen();
    }
  };

  const target = [header?.target_user, header?.target_host || header?.title]
    .filter(Boolean)
    .join("@");

  const passedMarkers = markers.filter((m) => m.time <= position);
  const currentMarker = passedMarkers[passedMarkers.length - 1];
  const redactions = markers.filter((m) => isRedactionMarker(m.data));

  if (!header && !events.length) {
    return (
      <div style={{ padding: 24, textAlign: "center", color: AN_COLORS.muted, fontFamily: AN_FONT, fontSize: 13 }}>
        This recording has no readable asciicast events.
        {errors.length > 0 && <div style={{ marginTop: 6, fontSize: 11.5 }}>{errors.length} unparseable line(s).</div>}
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 12, fontFamily: AN_FONT }}>
      {windowFill && <div aria-hidden style={{ height: placeholderH }} />}
      <div
        ref={playerRef}
        tabIndex={0}
        onKeyDown={onKeyDown}
        style={
          isFullscreen
            ? {
                // Centred vertically: a cropped (wide, short) console would
                // otherwise sit at the top with the screen empty below it.
                display: "grid", gap: 12, alignContent: "center", background: "#05060A", padding: FULLSCREEN_PAD, outline: "none", boxSizing: "border-box",
                ...(windowFill
                  ? { position: "fixed", inset: 0, zIndex: 2000 }
                  : { position: "relative", width: "100%", height: "100%" }),
              }
            : { position: "relative", display: "grid", gap: 12, outline: "none" }
        }
      >
      {isFullscreen && (
        <Tooltip title="Exit full screen (Esc)" placement="left">
          <button
            type="button"
            onClick={toggleFullscreen}
            aria-label="Exit full screen"
            style={{
              position: "absolute", top: 24, right: 24, zIndex: 2, width: 34, height: 34, borderRadius: "50%",
              display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
              border: "1px solid rgba(255,255,255,0.25)", background: "rgba(20,22,30,0.85)", color: "#fff",
            }}
          >
            <X size={18} />
          </button>
        </Tooltip>
      )}

      {/* Terminal — the whole recorded screen, scaled to fit the space left
          on screen. The frame hugs it; the area around it stays empty. */}
      <div
        ref={areaRef}
        style={{
          // No taller than the frame, so the controls sit right under the console.
          height: natural.h ? Math.min(boxHeight, natural.h * scale + FRAME) : boxHeight,
          display: "flex", justifyContent: "center", alignItems: "flex-start", overflow: "hidden",
        }}
      >
        <div
          style={{
            width: natural.w ? natural.w * scale + FRAME : "100%",
            height: natural.h ? natural.h * scale + FRAME : "100%",
            background: TERM_THEME.background,
            border: `${BOX_BORDER}px solid ${AN_COLORS.ink2}`,
            borderRadius: 9,
            padding: BOX_PAD,
            boxSizing: "border-box",
            overflow: "hidden",
            flexShrink: 0,
            position: "relative",
            cursor: playing ? "pointer" : undefined,
          }}
          // Like a video: clicking the console while it plays pauses it.
          onClick={playing ? togglePlay : undefined}
        >
          {/* The host is taken out of flow: a transform doesn't shrink layout
              size, so in flow its unscaled box would overflow the frame. */}
          <div
            style={{
              position: "relative",
              overflow: "hidden",
              width: natural.w ? natural.w * scale : undefined,
              height: natural.h ? natural.h * scale : undefined,
            }}
          >
            <div
              ref={hostRef}
              className="an-cast-host"
              style={{ position: "absolute", top: 0, left: 0, transform: `scale(${scale})`, transformOrigin: "top left", width: "max-content" }}
            />
          </div>

          {/* Paused or finished: a play (or replay) button over the console,
              the whole frame clickable, as on a video player. */}
          {!playing && (
            <button
              type="button"
              onClick={togglePlay}
              aria-label={ended ? "Replay" : position > 0 ? "Resume" : "Play"}
              style={{
                position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
                border: "none", padding: 0, cursor: "pointer", background: "rgba(5, 6, 10, 0.35)",
              }}
            >
              <span
                style={{
                  width: 64, height: 64, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center",
                  background: "rgba(43, 63, 232, 0.92)", color: "#fff", boxShadow: "0 6px 24px rgba(0,0,0,0.45)",
                }}
              >
                {ended ? <RotateCcw size={28} /> : <Play size={30} style={{ marginLeft: 4 }} fill="#fff" />}
              </span>
            </button>
          )}
        </div>
      </div>
      <style>{`.an-cast-host .xterm-viewport { overflow: hidden !important; }`}</style>

      {/* Transport */}
      <div
        ref={transportRef}
        style={{
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
          ...(isFullscreen ? { background: AN_COLORS.surface, borderRadius: 9, padding: "8px 12px" } : {}),
        }}
      >
        <button
          type="button"
          onClick={togglePlay}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px",
            border: "none", borderRadius: 7, background: AN_COLORS.primary, color: "#fff",
            fontFamily: AN_FONT, fontSize: 12.5, fontWeight: 500, cursor: "pointer",
          }}
        >
          {playing ? <Pause size={14} /> : <Play size={14} />}
          {playing ? "Pause" : "Play"}
        </button>

        <Tooltip title="Replay from the beginning">
          <button
            type="button"
            onClick={restart}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 11px",
              border: `1px solid ${AN_COLORS.line}`, borderRadius: 7, background: AN_COLORS.surface,
              color: AN_COLORS.ink2, fontFamily: AN_FONT, fontSize: 12.5, cursor: "pointer",
            }}
          >
            <RotateCcw size={13} />
            Restart
          </button>
        </Tooltip>

        <span style={{ fontFamily: AN_MONO, fontSize: 12, color: AN_COLORS.ink2, whiteSpace: "nowrap" }}>
          {formatClock(position)} / {formatClock(duration)}
        </span>

        <div style={{ flex: "1 1 220px", minWidth: 180 }}>
          <Slider
            min={0}
            max={Math.max(duration, 0.001)}
            step={0.05}
            value={Math.min(position, duration)}
            onChange={seek}
            tooltip={{ formatter: formatClock }}
          />
        </div>

        <div style={{ display: "inline-flex", border: `1px solid ${AN_COLORS.line}`, borderRadius: 7, overflow: "hidden" }}>
          {SPEEDS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSpeed(s)}
              style={{
                padding: "5px 11px", border: "none", cursor: "pointer",
                background: speed === s ? AN_COLORS.primaryL : AN_COLORS.surface,
                color: speed === s ? AN_COLORS.primaryD : AN_COLORS.ink2,
                fontFamily: AN_MONO, fontSize: 11.5, fontWeight: 600,
              }}
            >
              {s}×
            </button>
          ))}
        </div>

        <Tooltip title="Under a terminal the target echoes what was typed, so keystrokes usually duplicate the output line for line.">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, color: AN_COLORS.ink2 }}>
            <Switch size="small" checked={showKeystrokes} onChange={setShowKeystrokes} />
            Show keystrokes
          </span>
        </Tooltip>

        <Tooltip title={isFullscreen ? "Exit full screen (Esc)" : "Full screen (F)"}>
          <button
            type="button"
            onClick={toggleFullscreen}
            aria-label={isFullscreen ? "Exit full screen" : "Full screen"}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 11px",
              border: `1px solid ${AN_COLORS.line}`, borderRadius: 7, background: AN_COLORS.surface,
              color: AN_COLORS.ink2, fontFamily: AN_FONT, fontSize: 12.5, cursor: "pointer",
            }}
          >
            {isFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
            {isFullscreen ? "Exit full screen" : "Full screen"}
          </button>
        </Tooltip>
      </div>
      </div>

      {/* Evidence panel. The header is the point of the recording as much as the
          terminal is: who, which key, which target, from where. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: 12,
          background: AN_COLORS.bg,
          border: `1px solid ${AN_COLORS.line}`,
          borderRadius: 9,
          padding: "12px 14px",
        }}
      >
        <HeaderField label="Principal">{header?.principal}</HeaderField>
        <HeaderField label="Key fingerprint" wide>{header?.key_fingerprint}</HeaderField>
        <HeaderField label="Target">{target}</HeaderField>
        <HeaderField label="Source IP">{header?.source_ip}</HeaderField>
        <HeaderField label="Session ID">{header?.session_id}</HeaderField>
      </div>

      {redactions.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 9,
            alignItems: "flex-start",
            background: "#FFF7E8",
            border: "1px solid #F3E1B8",
            color: "#8A5A00",
            borderRadius: 8,
            padding: "10px 12px",
            fontSize: 12.5,
            lineHeight: 1.5,
          }}
        >
          <EyeOff size={15} style={{ marginTop: 1, flexShrink: 0 }} />
          <div>
            {redactions.length} point{redactions.length === 1 ? "" : "s"} in this session where typed input was
            withheld — the gateway drops bytes it never saw echoed back, which is what a password at a{" "}
            <code style={{ fontFamily: AN_MONO }}>sudo</code> prompt looks like. The gap is marked rather than
            silent; the keystrokes were never recorded.
          </div>
        </div>
      )}

      {/* Markers — annotations, never terminal text. */}
      {markers.length > 0 && (
        <div style={{ border: `1px solid ${AN_COLORS.line}`, borderRadius: 9, padding: "10px 12px", background: AN_COLORS.surface }}>
          <div style={{ fontFamily: AN_MONO, fontSize: 9.5, letterSpacing: 0.9, textTransform: "uppercase", color: AN_COLORS.muted, marginBottom: 8 }}>
            Markers {currentMarker ? `· now: ${currentMarker.data}` : ""}
          </div>
          <div style={{ display: "grid", gap: 4, maxHeight: 130, overflowY: "auto" }}>
            {markers.map((m, i) => {
              const redacted = isRedactionMarker(m.data);
              const passed = m.time <= position;
              return (
                <button
                  key={`${m.time}-${i}`}
                  type="button"
                  onClick={() => seek(m.time)}
                  style={{
                    display: "flex", alignItems: "center", gap: 9, textAlign: "left", width: "100%",
                    padding: "5px 8px", borderRadius: 6, cursor: "pointer",
                    border: `1px solid ${redacted ? "#F3E1B8" : AN_COLORS.line2}`,
                    background: redacted ? "#FFF7E8" : passed ? AN_COLORS.tint : AN_COLORS.surface,
                    color: redacted ? "#8A5A00" : AN_COLORS.ink2,
                    fontFamily: AN_FONT, fontSize: 12,
                  }}
                >
                  <span style={{ fontFamily: AN_MONO, fontSize: 11, color: AN_COLORS.muted, flexShrink: 0 }}>
                    {formatClock(m.time)}
                  </span>
                  {redacted && <EyeOff size={12} style={{ flexShrink: 0 }} />}
                  <span style={{ wordBreak: "break-word" }}>{m.data || "(marker)"}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {errors.length > 0 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 11.5, color: AN_COLORS.warn }}>
          <AlertTriangle size={13} />
          {errors.length} line(s) in this recording could not be parsed and were skipped.
        </div>
      )}
    </div>
  );
}
